import pdfIcon from "./assets/pdf_icon.svelte";
import docIcon from "./assets/doc_icon.svelte";
import docxIcon from "./assets/docx_icon.svelte";
import csvIcon from "./assets/csv_icon.svelte";

export const stripHtmlExtensionFromUrl = (url: string): string => {
  try {
    const parsed = new URL(url);
    if (!/\.html$/i.test(parsed.pathname)) {
      return url;
    }
    parsed.pathname = parsed.pathname.replace(/\.html$/i, "");
    return parsed.toString();
  } catch {
    return url;
  }
};

export const formatSegment = (
  segment: string,
  index?: number,
  getIndexForwardFields?: boolean
): string => {
  if (!segment) {
    return "";
  }

  const formattedSegment = segment
    ?.replaceAll("-", " ")
    ?.toLowerCase()
    ?.split(" ")
    ?.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    ?.join(" ");

  if (typeof index === "number" && index >= 0) {
    if (getIndexForwardFields) {
      const words = formattedSegment?.split(" ");
      return words?.slice(index)?.join(" ");
    } else {
      return formattedSegment?.split(" ")?.[index];
    }
  } else {
    return formattedSegment;
  }
};

export const capitalizeMatchingPart = (text: string, searchTerm: string) => {
  if (!text || !searchTerm) return "";

  const words = text.split(" ");
  return words
    .map((word) => {
      if (word.toLowerCase() === searchTerm.toLowerCase()) {
        return word.toUpperCase();
      }
      return word;
    })
    .join(" ");
};

export const getFileExtension = (
  fileName?: string,
  contentType?: string
): string => {
  if (contentType) {
    return contentType.split("/").pop()!.toLowerCase();
  }

  if (fileName) {
    return fileName.split(".").pop()!.toLowerCase();
  }

  return "document";
};

export const downloadFile = async (url: string, fileName?: string) => {
  try {
    const fetchResponse = await fetch(url, {
      cache: "no-store",
    });
    const blob = await fetchResponse.blob();

    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = fileName || "download";

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    window.URL.revokeObjectURL(blobUrl);
  } catch (error) {
    console.error("Failed to download file:", error);
  }
};

export const isImage = (file: {
  content_type?: string;
  file_name?: string;
}): boolean => {
  return file?.content_type?.startsWith("image/") || false;
};

export const isPdf = (file: {
  content_type?: string;
  file_name?: string;
}): boolean => {
  const contentType = file?.content_type || "";
  const fileName = file?.file_name || "";
  return (
    contentType === "application/pdf" || fileName.toLowerCase().endsWith(".pdf")
  );
};

export const isText = (file: {
  content_type?: string;
  file_name?: string;
}): boolean => {
  const contentType = file?.content_type || "";
  const fileName = file?.file_name || "";
  const textTypes = ["text/plain", "text/csv", "text/html", "text/css"];
  const textExtensions = [".txt", ".csv", ".html", ".css", ".js", ".ts"];
  return (
    textTypes?.includes(contentType) ||
    textExtensions?.some((ext) => fileName?.toLowerCase().endsWith(ext))
  );
};

export const isMicrosoftOffice = (file: {
  content_type?: string;
  file_name?: string;
}): boolean => {
  const contentType = file?.content_type || "";
  const fileName = file?.file_name || "";

  const officeTypes = [
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document", // .docx
    "application/vnd.openxmlformats-officedocument.presentationml.presentation", // .pptx
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // .xlsx
    "application/msword", // .doc
    "application/vnd.ms-powerpoint", // .ppt
    "application/vnd.ms-excel", // .xls
  ];

  const officeExtensions = [".docx", ".pptx", ".xlsx", ".doc", ".ppt", ".xls"];

  return (
    officeTypes.includes(contentType) ||
    officeExtensions.some((ext) => fileName?.toLowerCase().endsWith(ext))
  );
};

export const isTextFile = (file: {
  content_type?: string;
  file_name?: string;
}): boolean => {
  const fileName = file?.file_name || "";
  const textExtensions = [".log", ".md", ".json", ".xml", ".yaml", ".yml"];
  return textExtensions?.some((ext) => fileName?.toLowerCase().endsWith(ext));
};

export const getDateAndTime = (timestamp: string) => {
  const date = new Date(timestamp);
  const formattedDate = date.toLocaleDateString([], {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
  const formattedTime = date.toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
  return {
    date: formattedDate,
    time: formattedTime,
  };
};

export const getFileTypeIcon = (
  fileName?: string,
  contentType?: string
): string => {
  if (!fileName && !contentType) {
    return "https://vf-assets.verbaflo.com/vf-email/vf-email/doc.svg";
  }

  const extension = getFileExtension(fileName, contentType);

  if (!extension) {
    return "https://vf-assets.verbaflo.com/vf-email/vf-email/doc.svg";
  }

  return `https://vf-assets.verbaflo.com/vf-email/vf-email/${extension}.svg`;
};
